import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Star, MapPin, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface Review {
  id: string;
  customer_name: string;
  rating: number;
  review_text: string;
  likes_count: number;
  created_at: string;
  product_name: string;
}

const CustomerReviewsSection = () => {
  // Fetch top reviews from database
  const { data: reviews = [], isLoading, error } = useQuery({
    queryKey: ['top-reviews'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('product_reviews')
          .select(`
            id,
            customer_name,
            rating,
            review_text,
            likes_count,
            created_at,
            product_id
          `)
          .order('likes_count', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) {
          console.error('Error fetching reviews:', error);
          return [];
        }

        // Fetch product names separately
        if (data && data.length > 0) {
          const productIds = data.map(r => r?.product_id).filter(Boolean);
          
          if (productIds.length === 0) return [];
          
          const { data: products } = await supabase
            .from('products')
            .select('id, name')
            .in('id', productIds);

          const productMap = new Map(products?.map(p => [p?.id, p?.name]) || []);

          return data.map(review => ({
            id: review?.id ?? '',
            customer_name: review?.customer_name ?? 'Anonymous',
            rating: review?.rating ?? 5,
            review_text: review?.review_text ?? '',
            likes_count: review?.likes_count ?? 0,
            created_at: review?.created_at ?? new Date().toISOString(),
            product_name: productMap.get(review?.product_id) ?? 'Product'
          }));
        }

        return [];
      } catch (err) {
        console.error('Error in reviews query:', err);
        return [];
      }
    },
    retry: false,
    staleTime: 60000, // Cache for 1 minute
  });

  // Fallback static testimonials if no reviews in database
  const staticTestimonials = [
    { 
      id: '1',
      customer_name: "Rajesh Kumar", 
      location: "Kharagpur", 
      review_text: "Bought my Samsung phone here. 100% genuine with proper bill and warranty. Best prices in town!", 
      rating: 5,
      product_name: "Samsung Galaxy"
    },
    { 
      id: '2',
      customer_name: "Priya Sharma", 
      location: "Midnapore", 
      review_text: "Amazing service! They helped me choose the perfect TV. Great after-sale support too.", 
      rating: 5,
      product_name: "LED TV"
    },
    { 
      id: '3',
      customer_name: "Amit Patel", 
      location: "Kolkata", 
      review_text: "I always buy my accessories from Debnath Telecom. Trustworthy and fair prices.", 
      rating: 4,
      product_name: "Accessories"
    },
  ];

  const displayReviews = (reviews?.length ?? 0) > 0 ? reviews.slice(0, 3) : staticTestimonials;

  // If there's an error or loading failed, just show static testimonials
  if (error) {
    console.error('Reviews error:', error);
  }

  if (isLoading) {
    return (
      <section className="section-padding bg-muted/30">
        <div className="container">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Reviews</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold">What Our Customers Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-muted rounded w-20 mb-4" />
                  <div className="space-y-2 mb-5">
                    <div className="h-3 bg-muted rounded" />
                    <div className="h-3 bg-muted rounded w-5/6" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-muted" />
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded w-24" />
                      <div className="h-2 bg-muted rounded w-16" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!displayReviews || displayReviews.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-muted/30">
      <div className="container">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Reviews</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold">What Our Customers Say</h2>
          {(reviews?.length ?? 0) > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Real reviews from verified customers
            </p>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {displayReviews?.map((review, i) => {
            if (!review) return null;
            
            const customerName = review?.customer_name ?? 'Anonymous';
            const rating = review?.rating ?? 5;
            const reviewText = review?.review_text ?? '';
            const productName = review?.product_name ?? 'Product';
            const reviewId = review?.id ?? `review-${i}`;
            
            return (
              <motion.div
                key={reviewId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-4">
                      {Array.from({ length: rating }).map((_, j) => (
                        <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-5 leading-relaxed flex-1">
                      "{reviewText}"
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-sm shrink-0">
                          {customerName?.[0] ?? 'A'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{customerName}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {productName}
                          </p>
                        </div>
                      </div>
                      
                      {(reviews?.length ?? 0) > 0 && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                          <MessageSquare className="h-3 w-3" />
                          <span>Verified Purchase</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
        
        {(reviews?.length ?? 0) > 3 && (
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              Showing {displayReviews?.length ?? 0} of {reviews?.length ?? 0} reviews
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomerReviewsSection;
